import { useState, useEffect } from 'react';
import type { GameType, RoomMode, SeatCount, BotDifficulty, GameSpeed, RoomConfig } from '@big2/shared';
import { Icon } from '../Icon.js';
import { useI18n } from '../../i18n.js';

interface LobbySettingsPanelProps {
  config: RoomConfig;
  isHost: boolean;
  isPaid: boolean;
  minTsai: number;
  supportedGames?: GameType[];
  onUpdateConfig: (updates: Partial<RoomConfig>) => void;
  onOpenProfile?: () => void;
}

export function LobbySettingsPanel({
  config,
  isHost,
  isPaid,
  minTsai,
  supportedGames,
  onUpdateConfig,
  onOpenProfile,
}: LobbySettingsPanelProps) {
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';

  const [optimisticUpdates, setOptimisticUpdates] = useState<Partial<RoomConfig>>({});

  useEffect(() => {
    setOptimisticUpdates({});
  }, [config]);

  const effectiveConfig: RoomConfig = {
    ...config,
    ...optimisticUpdates,
  };

  const handleUpdate = (updates: Partial<RoomConfig>) => {
    const nextGameType = updates.gameType ?? effectiveConfig.gameType ?? 'big2';
    const nextSeats = updates.seats ?? effectiveConfig.seats;
    if (nextGameType === 'daaluu') {
      updates.seats = 5;
    } else if (nextGameType === 'muushig' && !updates.seats && effectiveConfig.gameType !== 'muushig') {
      updates.seats = 5;
    } else if (nextGameType === 'big2' && !updates.seats && effectiveConfig.gameType !== 'big2') {
      updates.seats = 4;
    } else if (nextGameType !== 'muushig' && nextSeats === 5) {
      updates.seats = 4;
    }
    setOptimisticUpdates((prev) => ({ ...prev, ...updates }));
    onUpdateConfig(updates);
  };

  const gameType: GameType = effectiveConfig.gameType ?? 'big2';
  const mode: RoomMode = effectiveConfig.mode ?? 'free';
  const seats: SeatCount = effectiveConfig.seats;
  const speed: GameSpeed = effectiveConfig.gameSpeed ?? 'normal';
  const botDifficulty: BotDifficulty = effectiveConfig.botDifficulty ?? 'medium';
  const pointLimit = effectiveConfig.pointLimit ?? 50;
  const followColor = effectiveConfig.daaluuFollowColor ?? true;
  const isMuushig = gameType === 'muushig';
  const isTsaiRoom = mode === 'tsai';

  const speedLabels: Record<GameSpeed, string> = {
    relaxed: isMn ? 'Удаан' : 'Slow',
    normal: isMn ? 'Дунд' : 'Medium',
    fast: isMn ? 'Хурдан' : 'Fast',
  };

  const speedTimers: Record<GameSpeed, string> = {
    relaxed: isMn ? '45с' : '45s',
    normal: isMn ? '30с' : '30s',
    fast: isMn ? '15с' : '15s',
  };

  const difficultyLabels: Record<BotDifficulty, string> = {
    easy: t.botDifficultyEasy,
    medium: t.botDifficultyMedium,
    hard: t.botDifficultyHard,
  };

  const availableSeats: SeatCount[] = [2, 3, 4, ...(isMuushig ? [5 as SeatCount] : [])];

  return (
    <section className="lobby-settings-section">
      <div className="lobby-section-title-bar">
        <h3>
          <span className="lobby-section-suit" aria-hidden="true">♦</span>
          <span>{t.settingsHeader}</span>
          {!isHost && (
            <span className="lobby-host-controls-note">
              ({isMn ? 'Эзэн тохируулна' : 'Host controls'})
            </span>
          )}
        </h3>
      </div>

      {isHost ? (
        <>
          <div className="lobby-settings-controls-grid">
            {/* Game Selection */}
            <div className="lobby-setting-group">
              <span className="lobby-setting-group-label">{isMn ? 'Тоглоом' : 'Game'}</span>
              <div className="segmented-control">
                <button
                  type="button"
                  className={`segmented-pill is-flagship-pill${gameType === 'big2' ? ' is-active' : ''}`}
                  onClick={() => handleUpdate({ gameType: 'big2' })}
                >
                  <span className="pill-suit" aria-hidden="true">♠</span>
                  <span>{isMn ? 'Арван гурав' : 'Big Two'}</span>
                </button>
                <button
                  type="button"
                  className={`segmented-pill${gameType === 'muushig' ? ' is-active' : ''}`}
                  onClick={() => handleUpdate({ gameType: 'muushig' })}
                >
                  <span className="pill-suit" aria-hidden="true">♣</span>
                  <span>{isMn ? 'Муушиг' : 'Muushig'}</span>
                </button>
                {supportedGames?.includes('twentyone') && (
                  <button
                    type="button"
                    className={`segmented-pill${gameType === 'twentyone' ? ' is-active' : ''}`}
                    onClick={() => handleUpdate({ gameType: 'twentyone' })}
                  >
                    <span className="pill-suit" aria-hidden="true">♦</span>
                    <span>{isMn ? '21 (Очко)' : '21'}</span>
                  </button>
                )}
                {supportedGames?.includes('daaluu') && (
                  <button
                    type="button"
                    className={`segmented-pill${gameType === 'daaluu' ? ' is-active' : ''}`}
                    onClick={() => handleUpdate({ gameType: 'daaluu' })}
                  >
                    <span className="pill-suit" aria-hidden="true">🂍</span>
                    <span>{isMn ? 'Даалуу' : 'Daaluu'}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Room Mode (Free vs Tsai) */}
            {gameType !== 'daaluu' && (
              <div className="lobby-setting-group">
                <span className="lobby-setting-group-label">
                  <span>{isMn ? 'Горим' : 'Mode'}</span>
                  {!isPaid && onOpenProfile && (
                    <button
                      type="button"
                      onClick={onOpenProfile}
                      className="lobby-vip-label-link"
                      title={isMn ? 'VIP эрх авах' : 'Get VIP'}
                    >
                      <Icon name="crown" size={11} /> {isMn ? 'VIP эрх шаардлагатай' : 'Requires VIP'}
                    </button>
                  )}
                </span>
                <div className="segmented-control">
                  <button
                    type="button"
                    className={`segmented-pill${mode === 'free' ? ' is-active' : ''}`}
                    onClick={() => handleUpdate({ mode: 'free' })}
                  >
                    {isMn ? 'Чөлөөт' : 'Free'}
                  </button>
                  <div
                    className="segmented-pill-wrap"
                    onClick={!isPaid && onOpenProfile ? onOpenProfile : undefined}
                  >
                    <button
                      type="button"
                      className={`segmented-pill${mode === 'tsai' ? ' is-active' : ''}${!isPaid ? ' is-vip-pill' : ''}`}
                      disabled={!isPaid}
                      onClick={() => {
                        if (!isPaid) {
                          onOpenProfile?.();
                          return;
                        }
                        handleUpdate({ mode: 'tsai' });
                      }}
                      title={!isPaid ? (isMn ? 'Цайтай өрөө нь зөвхөн VIP эрхтэй хэрэглэгчдэд нээлттэй' : 'Tsai rooms require a VIP profile') : undefined}
                    >
                      <span>🍵 {isMn ? 'Цайтай' : 'Tsai Room'}</span>
                      {!isPaid && (
                        <span className="room-vip-pill-badge">
                          <Icon name="crown" size={11} /> VIP
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Seat Count */}
            {gameType !== 'daaluu' && (
              <div className="lobby-setting-group">
                <span className="lobby-setting-group-label">{isMn ? 'Суудлын тоо' : 'Seats'}</span>
                <div className="segmented-control">
                  {availableSeats.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`segmented-pill${seats === s ? ' is-active' : ''}`}
                      onClick={() => handleUpdate({ seats: s })}
                    >
                      {s} {isMn ? 'суудал' : 'seats'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Game Speed */}
            <div className="lobby-setting-group">
              <span className="lobby-setting-group-label">{isMn ? 'Хурд' : 'Speed'}</span>
              <div className="segmented-control">
                {(['relaxed', 'normal', 'fast'] as GameSpeed[]).map((spd) => (
                  <button
                    key={spd}
                    type="button"
                    className={`segmented-pill${speed === spd ? ' is-active' : ''}`}
                    onClick={() => handleUpdate({ gameSpeed: spd })}
                    title={`${speedLabels[spd]} (${speedTimers[spd]})`}
                  >
                    <span>{speedLabels[spd]}</span>
                    <span className="pill-hint">
                      ({speedTimers[spd]})
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Point Limit (Big Two only) */}
            {gameType === 'big2' && (
              <div className="lobby-setting-group">
                <span className="lobby-setting-group-label">
                  {isMn ? 'Онооны хязгаар' : 'Point limit'}
                </span>
                <div className="segmented-control">
                  {[30, 40, 50, 75, 100].map((limit) => (
                    <button
                      key={limit}
                      type="button"
                      className={`segmented-pill${pointLimit === limit ? ' is-active' : ''}`}
                      onClick={() => handleUpdate({ pointLimit: limit })}
                    >
                      {`${limit} ${isMn ? 'оноо' : 'pts'}`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Bot Difficulty */}
            <div className="lobby-setting-group">
              <span className="lobby-setting-group-label">{t.botDifficulty}</span>
              <div className="segmented-control">
                {(['easy', 'medium', 'hard'] as BotDifficulty[]).map((diff) => (
                  <button
                    key={diff}
                    type="button"
                    className={`segmented-pill${botDifficulty === diff ? ' is-active' : ''}`}
                    onClick={() => handleUpdate({ botDifficulty: diff })}
                  >
                    {difficultyLabels[diff]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tsai room warning badge */}
          {isTsaiRoom && (
            <div className="lobby-tsai-notice">
              🍵 {t.minTsaiRequiredBadge(minTsai)}
            </div>
          )}

          {/* Toggles (Bots, Public, Voice) */}
          <div className="lobby-toggles-grid">
            {/* Daaluu rule: replies must follow the led color (default on) */}
            {gameType === 'daaluu' && (
              <label
                className={`lobby-toggle-card${followColor ? ' is-active' : ''}`}
                title={isMn
                  ? 'Унтраавал тоглогдсон өнгийг дагах албагүй: жишээ нь улаан хос гарахад ганц улаан даалуугаа хадгалж, жижиг цагаан мод өгч болно.'
                  : 'When off, replies need not follow the led color: e.g. against a red pair you may keep a lone red 12 and give small white tiles.'}
              >
                <span className="lobby-toggle-label">
                  <span>{isMn ? 'Өнгө дагах' : 'Follow color'}</span>
                </span>
                <input
                  type="checkbox"
                  className="lobby-toggle-input"
                  checked={followColor}
                  onChange={(e) => handleUpdate({ daaluuFollowColor: e.target.checked })}
                />
              </label>
            )}

            {/* Fill with bots */}
            <label className={`lobby-toggle-card${(effectiveConfig.fillWithBots ?? config.fillWithBots) && !isTsaiRoom ? ' is-active' : ''}`}>
              <span className="lobby-toggle-label">
                <span>{isMn ? 'Ботоор дүүргэх' : 'Fill with bots'}</span>
              </span>
              <input
                type="checkbox"
                className="lobby-toggle-input"
                checked={isTsaiRoom ? false : (effectiveConfig.fillWithBots ?? config.fillWithBots)}
                disabled={isTsaiRoom}
                onChange={(e) => handleUpdate({ fillWithBots: e.target.checked })}
              />
            </label>

            {/* Voice chat */}
            <div className="lobby-toggle-col">
              <label
                className={`lobby-toggle-card${(effectiveConfig.voiceChatEnabled ?? config.voiceChatEnabled) ? ' is-active' : ''}${!isPaid ? ' is-vip-locked' : ''}`}
                onClick={!isPaid && onOpenProfile ? (e) => { e.preventDefault(); onOpenProfile(); } : undefined}
                title={!isPaid ? (isMn ? 'VIP эрх авч дуут яриа идэвхжүүлэх' : 'Unlock Voice Chat with VIP') : undefined}
              >
                <span className="lobby-toggle-label">
                  <span>{isMn ? 'Өрөөний дуут яриа' : 'Room voice chat'}</span>
                  {!isPaid && (
                    <span className="room-vip-tag">
                      <Icon name="crown" size={12} /> VIP
                    </span>
                  )}
                </span>
                <input
                  type="checkbox"
                  className="lobby-toggle-input"
                  checked={!!(effectiveConfig.voiceChatEnabled ?? config.voiceChatEnabled)}
                  disabled={!isPaid}
                  onChange={(e) => isPaid && handleUpdate({ voiceChatEnabled: e.target.checked })}
                />
              </label>

              {!isPaid && onOpenProfile && (
                <div className="lobby-toggle-vip-hint">
                  <button
                    type="button"
                    onClick={onOpenProfile}
                    className="lobby-vip-inline-link"
                  >
                    👑 {isMn ? 'VIP эрх авч дуут яриа идэвхжүүлэх' : 'Unlock Voice Chat with VIP'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        /* Guest View: Architectural Match Specification Ledger */
        <div className="lobby-guest-rules-card">
          <p className="lobby-guest-rules-intro">
            {isMn
              ? 'Өрөөний эзний тохируулсан тоглоомын дүрэм:'
              : 'Match settings configured by the host:'}
          </p>

          <div className="lobby-rule-badge-row">
            <div className="lobby-rule-badge">
              <span className="lobby-rule-label">{isMn ? 'Тоглоом' : 'Game'}</span>
              <strong>{gameType === 'daaluu' ? 'Даалуу' : gameType === 'twentyone' ? '21 (Очко)' : isMuushig ? 'Муушиг' : 'Арван гурав'}</strong>
            </div>
            <div className="lobby-rule-badge">
              <span className="lobby-rule-label">{isMn ? 'Суудал' : 'Seats'}</span>
              <strong>{seats} {isMn ? 'тоглогч' : 'seats'}</strong>
            </div>
            <div className="lobby-rule-badge">
              <span className="lobby-rule-label">{isMn ? 'Хурд' : 'Speed'}</span>
              <strong>{speedLabels[speed]} ({speedTimers[speed]})</strong>
            </div>
            {gameType === 'big2' && (
              <div className="lobby-rule-badge">
                <span className="lobby-rule-label">{isMn ? 'Оноо' : 'Target'}</span>
                <strong>{`${pointLimit} ${isMn ? 'онооны хязгаар' : 'pts limit'}`}</strong>
              </div>
            )}
            {gameType === 'daaluu' && (
              <div className="lobby-rule-badge">
                <span className="lobby-rule-label">{isMn ? 'Өнгө дагах' : 'Follow color'}</span>
                <strong>{followColor ? (isMn ? 'Тийм' : 'Yes') : (isMn ? 'Үгүй' : 'No')}</strong>
              </div>
            )}
            <div className="lobby-rule-badge">
              <span className="lobby-rule-label">{isMn ? 'Бот' : 'Bots'}</span>
              <strong>{config.fillWithBots ? `${isMn ? 'Бот дүүргэнэ' : 'Bots enabled'} (${difficultyLabels[botDifficulty]})` : (isMn ? 'Зөвхөн хүмүүс' : 'Humans only')}</strong>
            </div>
            <div className="lobby-rule-badge">
              <span className="lobby-rule-label">{isMn ? 'Горим' : 'Mode'}</span>
              <strong>{mode === 'tsai' ? `🍵 ${isMn ? 'Цайтай' : 'Tsai room'}` : (isMn ? 'Чөлөөт' : 'Free')}</strong>
            </div>
            {config.voiceChatEnabled && (
              <div className="lobby-rule-badge is-gold">
                <span className="lobby-rule-label">{isMn ? 'Дуут яриа' : 'Voice'}</span>
                <strong>{isMn ? 'Идэвхтэй' : 'Voice Chat'}</strong>
              </div>
            )}
          </div>

          {isTsaiRoom && (
            <div className="lobby-tsai-notice">
              🍵 {t.minTsaiRequiredBadge(minTsai)}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
